/**
 * TanStack Query hooks — the only place the app names an endpoint.
 *
 * Query keys are shaped so an invalidation after a movement refreshes exactly
 * what changed: the stock summary, the inventory table and the history, without
 * blowing away the whole cache.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { apiFetch, type RequestOptions } from "./client";
import type {
  BatchAnalyseResult,
  KnownSupplier,
  EmailStatus,
  InboundClassification,
  InboundMessage,
  OutboundMessage,
  OutboundMessageStatus,
  PolicyChangeList,
  ProcurementOverview,
  Rfq,
  RfqDispatchRow,
  RfqDetail,
  RfqDispatchPreview,
  RfqLine,
  RfqRequestedTerm,
  RfqStatus,
  PolicyMode,
  PolicyRecommendation,
  PolicyRecommendationList,
  ConfirmPreview,
  NetRequirements,
  ProjectRequirementState,
  RequirementPage,
  SuggestionPage,
  EstimateLineType,
  EstimateVersionDetail,
  MaterialList,
  MaterialListDetail,
  MaterialListLineStatus,
  PlanningPriority,
  MaterialListStatus,
  MaterialListType,
  ProjectEstimate,
  ProjectEstimateDetail,
  ProjectEstimateStatus,
  UnitCode,
  CapabilityPage,
  Conversation,
  ConversationAttachment,
  ConversationDetail,
  ConversationMessagePage,
  ConversationPage,
  ConversationStatus,
  FieldProvenance,
  DecisionPayload,
  DecisionResult,
  MessageExchange,
  InventoryPolicy,
  ProductPrice,
  ProductCapability,
  ProductCapabilityList,
  ProductCapabilityWrite,
  ProductRelation,
  ProductRelations,
  ProductRelationWrite,
  SupplierProduct,
  ProductPackage,
  AIDiagnostics,
  Asset,
  AssetCondition,
  AssetLifecycle,
  AssistantAnswer,
  CategoryNode,
  ConfirmResult,
  Container,
  ContainerContents,
  CoverageLine,
  DashboardSummary,
  DocumentIndexStatus,
  EventEntry,
  Identifier,
  IngestionMode,
  IngestionSession,
  InventoryRow,
  InventoryTransaction,
  Loan,
  LoanStatus,
  LoanSummary,
  Location,
  LocationNode,
  LocationPathStep,
  Media,
  MediaType,
  ValueSource,
  Organization,
  Page,
  Person,
  PersonHolding,
  PersonType,
  PlaceRef,
  Product,
  ProductAttribute,
  Project,
  ProjectDetail,
  ProjectStatus,
  PurchaseOrder,
  PurchaseOrderDetail,
  PurchaseOrderStatus,
  Quantity,
  ReasonCode,
  Receipt,
  ReservationResult,
  SearchResults,
  StockSummary,
  Supplier,
  TransactionPage,
  User,
  Uuid,
} from "./types";

export const queryKeys = {
  me: ["me"] as const,
  dashboard: ["dashboard"] as const,
  procurementDashboard: ["dashboard", "procurement"] as const,
  search: (q: string) => ["search", q] as const,
  inventory: (params: object) => ["inventory", "overview", params] as const,
  product: (id: Uuid) => ["product", id] as const,
  productAttributes: (id: Uuid) => ["product", id, "attributes"] as const,
  productIdentifiers: (id: Uuid) => ["product", id, "identifiers"] as const,
  productMedia: (id: Uuid) => ["product", id, "media"] as const,
  documentIndex: (id: Uuid) => ["media", id, "index"] as const,
  stockSummary: (id: Uuid) => ["stock", "summary", id] as const,
  transactions: (params: object) => ["transactions", params] as const,
  categories: ["categories", "tree"] as const,
  locationTree: ["locations", "tree"] as const,
  location: (id: Uuid) => ["location", id] as const,
  locationPath: (id: Uuid) => ["location", id, "path"] as const,
  containers: (params: object) => ["containers", params] as const,
  containerContents: (id: Uuid) => ["container", id, "contents"] as const,
  events: (params: object) => ["events", params] as const,
};

type QueryOpts<T> = Omit<UseQueryOptions<T, Error, T, readonly unknown[]>, "queryKey" | "queryFn">;

function get<T>(path: string, options?: RequestOptions) {
  return () => apiFetch<T>(path, options);
}

/* ---------------------------------------------------------------- session -- */

export function useOrganization(options?: QueryOpts<Organization>) {
  return useQuery({
    queryKey: ["organization"],
    queryFn: get<Organization>("/organization"),
    // Branding changes at deploy time, not during a shift.
    staleTime: 30 * 60_000,
    ...options,
  });
}

export function useMe(options?: QueryOpts<User>) {
  return useQuery({ queryKey: queryKeys.me, queryFn: get<User>("/auth/me"), ...options });
}

/* -------------------------------------------------------------- dashboard -- */

export function useDashboard(options?: QueryOpts<DashboardSummary>) {
  return useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: get<DashboardSummary>("/dashboard/summary"),
    ...options,
  });
}

/**
 * L'approvisionnement du dashboard, en **un** appel (V1.6-DASHBOARD).
 *
 * Sept modules dans une réponse plutôt que sept requêtes depuis le navigateur.
 * La clé commence par `dashboard`, donc toute invalidation du dashboard après
 * un mouvement rafraîchit aussi cette vue.
 */
export function useProcurementDashboard(options?: QueryOpts<ProcurementOverview>) {
  return useQuery({
    queryKey: queryKeys.procurementDashboard,
    queryFn: get<ProcurementOverview>("/dashboard/procurement"),
    ...options,
  });
}

/* ----------------------------------------------------------------- search -- */

export function useSearch(query: string, options?: QueryOpts<SearchResults>) {
  return useQuery({
    queryKey: queryKeys.search(query),
    queryFn: get<SearchResults>("/search", { query: { q: query } }),
    enabled: query.trim().length > 0,
    ...options,
  });
}

/* -------------------------------------------------------------- inventory -- */

export interface InventoryParams {
  /** Serialised straight into the query string. */
  [key: string]: unknown;
  q?: string;
  category_id?: Uuid;
  status?: string;
  location_id?: Uuid;
  container_id?: Uuid;
  in_stock_only?: boolean;
  limit?: number;
  offset?: number;
}

export function useInventory(params: InventoryParams, options?: QueryOpts<Page<InventoryRow>>) {
  return useQuery({
    queryKey: queryKeys.inventory(params),
    queryFn: get<Page<InventoryRow>>("/inventory/overview", { query: params }),
    ...options,
  });
}

export function useStockSummary(productId: Uuid, options?: QueryOpts<StockSummary>) {
  return useQuery({
    queryKey: queryKeys.stockSummary(productId),
    queryFn: get<StockSummary>(`/inventory/summary/${productId}`),
    ...options,
  });
}

export interface TransactionParams {
  /** Serialised straight into the query string. */
  [key: string]: unknown;
  product_id?: Uuid;
  location_id?: Uuid;
  container_id?: Uuid;
  limit?: number;
  offset?: number;
}

export function useTransactions(params: TransactionParams, options?: QueryOpts<TransactionPage>) {
  return useQuery({
    queryKey: queryKeys.transactions(params),
    queryFn: get<TransactionPage>("/inventory/transactions", { query: params }),
    ...options,
  });
}

/* --------------------------------------------------------------- products -- */

export function useProduct(productId: Uuid, options?: QueryOpts<Product>) {
  return useQuery({
    queryKey: queryKeys.product(productId),
    queryFn: get<Product>(`/products/${productId}`),
    ...options,
  });
}

export function useProductAttributes(productId: Uuid, options?: QueryOpts<ProductAttribute[]>) {
  return useQuery({
    queryKey: queryKeys.productAttributes(productId),
    queryFn: get<ProductAttribute[]>(`/products/${productId}/attributes`),
    ...options,
  });
}

export function useProductIdentifiers(productId: Uuid, options?: QueryOpts<Identifier[]>) {
  return useQuery({
    queryKey: queryKeys.productIdentifiers(productId),
    queryFn: get<Identifier[]>(`/products/${productId}/identifiers`),
    ...options,
  });
}

export function useProductMedia(productId: Uuid, options?: QueryOpts<Media[]>) {
  return useQuery({
    queryKey: queryKeys.productMedia(productId),
    queryFn: get<Media[]>(`/products/${productId}/media`),
    ...options,
  });
}

export function useCategories(options?: QueryOpts<CategoryNode[]>) {
  return useQuery({
    queryKey: queryKeys.categories,
    queryFn: get<CategoryNode[]>("/categories/tree"),
    ...options,
  });
}

/* -------------------------------------------------- locations & containers -- */

export function useLocationTree(options?: QueryOpts<LocationNode[]>) {
  return useQuery({
    queryKey: queryKeys.locationTree,
    queryFn: get<LocationNode[]>("/locations/tree"),
    ...options,
  });
}

export function useLocation(locationId: Uuid, options?: QueryOpts<Location>) {
  return useQuery({
    queryKey: queryKeys.location(locationId),
    queryFn: get<Location>(`/locations/${locationId}`),
    ...options,
  });
}

export function useLocationPath(locationId: Uuid, options?: QueryOpts<LocationPathStep[]>) {
  return useQuery({
    queryKey: queryKeys.locationPath(locationId),
    queryFn: get<LocationPathStep[]>(`/locations/${locationId}/path`),
    ...options,
  });
}

export function useContainers(
  params: { location_id?: Uuid; include_inactive?: boolean },
  options?: QueryOpts<Container[]>,
) {
  return useQuery({
    queryKey: queryKeys.containers(params),
    queryFn: get<Container[]>("/containers", { query: params }),
    ...options,
  });
}

export function useContainerContents(containerId: Uuid, options?: QueryOpts<ContainerContents>) {
  return useQuery({
    queryKey: queryKeys.containerContents(containerId),
    queryFn: get<ContainerContents>(`/containers/${containerId}/contents`),
    ...options,
  });
}

/* ------------------------------------------------------------------ audit -- */

export interface EventParams {
  /** Serialised straight into the query string. */
  [key: string]: unknown;
  entity_type?: string;
  entity_id?: Uuid;
  event_type?: string;
  correlation_id?: string;
  limit?: number;
  offset?: number;
}

export function useEvents(params: EventParams, options?: QueryOpts<Page<EventEntry>>) {
  return useQuery({
    queryKey: queryKeys.events(params),
    queryFn: get<Page<EventEntry>>("/events", { query: params }),
    ...options,
  });
}

/* -------------------------------------------------------------- mutations -- */

export interface MovementInput {
  product_id: Uuid;
  quantity: Quantity;
  source?: PlaceRef;
  destination?: PlaceRef;
  place?: PlaceRef;
  reason_code?: ReasonCode;
  idempotency_key?: string;
  target_quantity?: Quantity;
  delta?: Quantity;
}

type MovementKind =
  | "receive"
  | "issue"
  | "return"
  | "transfer"
  | "scrap"
  | "adjust"
  | "reserve"
  | "release-reservation";

const MOVEMENT_LABELS: Record<MovementKind, string> = {
  receive: "Réception",
  issue: "Sortie",
  return: "Retour",
  transfer: "Transfert",
  scrap: "Rebut",
  adjust: "Ajustement",
  reserve: "Réservation",
  "release-reservation": "Libération",
};

/** What the operator did, in their words: the queue shows this, not a URL. */
function movementLabel(kind: MovementKind, input: MovementInput): string {
  const quantity = input.quantity ?? input.target_quantity ?? input.delta ?? "";
  return `${MOVEMENT_LABELS[kind]}${quantity ? ` de ${String(quantity)}` : ""}`;
}

/**
 * Post a stock movement.
 *
 * Invalidates everything a movement can change. The frontend never adjusts a
 * cached quantity itself — it asks the server again, because the server is the
 * one that knows what the ledger did.
 */
export function useMovement(
  kind: MovementKind,
  options?: UseMutationOptions<InventoryTransaction, Error, MovementInput>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: MovementInput) =>
      apiFetch<InventoryTransaction>(`/inventory/${kind}`, {
        method: "POST",
        body: input,
        // Queued only when the caller supplied a key: replaying a movement
        // without one could post it twice (ADR 0009).
        offline: input.idempotency_key
          ? { label: movementLabel(kind, input), idempotencyKey: input.idempotency_key }
          : undefined,
      }),
    ...options,
    onSuccess: (data, variables, context) => {
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
      void queryClient.invalidateQueries({ queryKey: ["stock"] });
      void queryClient.invalidateQueries({ queryKey: ["transactions"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
      void queryClient.invalidateQueries({ queryKey: ["container"] });
      // Forward every argument so a caller's own onSuccess keeps its contract.
      (options?.onSuccess as ((...args: unknown[]) => void) | undefined)?.(
        data,
        variables,
        context,
      );
    },
  });
}

export interface CreateLocationInput {
  /** The customer's words. The code is HD-Vault's to allocate. */
  name: string;
  location_type: string;
  parent_id?: Uuid | null;
  description?: string;
  /** Only for importing an existing labelling scheme. */
  code?: string;
}

export function useCreateLocation(
  options?: UseMutationOptions<Location, Error, CreateLocationInput>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLocationInput) =>
      apiFetch<Location>("/locations", { method: "POST", body: input }),
    ...options,
    onSuccess: (data, variables, context) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.locationTree });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
      // Forward every argument so a caller's own onSuccess keeps its contract.
      (options?.onSuccess as ((...args: unknown[]) => void) | undefined)?.(
        data,
        variables,
        context,
      );
    },
  });
}

export interface MoveContainerInput {
  containerId: Uuid;
  location_id?: Uuid;
  parent_container_id?: Uuid;
}

/**
 * Move a container.
 *
 * One call moves everything inside it: the contents resolve their location
 * through this container, so nothing else has to be rewritten.
 */
export function useMoveContainer(
  options?: UseMutationOptions<Container, Error, MoveContainerInput>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ containerId, ...body }: MoveContainerInput) =>
      apiFetch<Container>(`/containers/${containerId}/move`, { method: "POST", body }),
    ...options,
    onSuccess: (data, variables, context) => {
      void queryClient.invalidateQueries({ queryKey: ["container"] });
      void queryClient.invalidateQueries({ queryKey: ["containers"] });
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
      // Forward every argument so a caller's own onSuccess keeps its contract.
      (options?.onSuccess as ((...args: unknown[]) => void) | undefined)?.(
        data,
        variables,
        context,
      );
    },
  });
}

/* --------------------------------------------- Smart Inventory Input (PHASE 6) */

export const ingestionKeys = {
  session: (id: Uuid) => ["ingestion", "session", id] as const,
  sessions: (params: object) => ["ingestion", "sessions", params] as const,
};

export function useIngestionSession(sessionId: Uuid, options?: QueryOpts<IngestionSession>) {
  return useQuery({
    queryKey: ingestionKeys.session(sessionId),
    queryFn: get<IngestionSession>(`/ingestion/sessions/${sessionId}`),
    enabled: Boolean(sessionId),
    ...options,
  });
}

/**
 * Every ingestion step returns the whole session, so the cache is replaced
 * rather than patched: the server owns which values won, and a client that
 * merged them itself would eventually disagree.
 */
function useSessionMutation<TInput>(
  path: (input: TInput) => string,
  method: "POST" | "PATCH" = "POST",
  body?: (input: TInput) => unknown,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<IngestionSession>(path(input), { method, body: body?.(input) }),
    onSuccess: (data) => {
      queryClient.setQueryData(ingestionKeys.session(data.id), data);
    },
  });
}

export function useStartIngestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      mode: IngestionMode;
      location_id?: Uuid | null;
      container_id?: Uuid | null;
    }) => apiFetch<IngestionSession>("/ingestion/sessions", { method: "POST", body: input }),
    onSuccess: (data) => {
      queryClient.setQueryData(ingestionKeys.session(data.id), data);
    },
  });
}

export function useAddIngestionPhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, file, role }: { sessionId: Uuid; file: File; role?: string }) => {
      const form = new FormData();
      form.append("file", file);
      if (role) form.append("role", role);
      return apiFetch<IngestionSession>(`/ingestion/sessions/${sessionId}/media`, {
        method: "POST",
        formData: form,
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(ingestionKeys.session(data.id), data);
    },
  });
}

export function useAnalyseIngestion() {
  return useSessionMutation<{ sessionId: Uuid; retryVision?: boolean }>(
    ({ sessionId, retryVision }) =>
      // `retry_vision` bypasses the server's "recently unavailable" cooldown on
      // the local model. It is the retry button and nothing else: an operator
      // who has just woken the Mac mini knows something the timer does not.
      `/ingestion/sessions/${sessionId}/analyze${retryVision ? "?retry_vision=true" : ""}`,
  );
}

export function useSetIngestionFields() {
  return useSessionMutation<{ sessionId: Uuid; values: Record<string, string | null> }>(
    ({ sessionId }) => `/ingestion/sessions/${sessionId}/fields`,
    "PATCH",
    ({ values }) => ({ values }),
  );
}

export function useChooseIngestionProduct() {
  return useSessionMutation<{
    sessionId: Uuid;
    product_id?: Uuid | null;
    create_new?: boolean;
  }>(
    ({ sessionId }) => `/ingestion/sessions/${sessionId}/product`,
    "POST",
    ({ product_id, create_new }) => ({ product_id: product_id ?? null, create_new: create_new ?? false }),
  );
}

export function useSetIngestionQuantity() {
  return useSessionMutation<{ sessionId: Uuid; quantity: string; unit?: string | null }>(
    ({ sessionId }) => `/ingestion/sessions/${sessionId}/quantity`,
    "POST",
    ({ quantity, unit }) => ({ quantity, unit: unit ?? null }),
  );
}

export function useSetIngestionDestination() {
  return useSessionMutation<{
    sessionId: Uuid;
    location_id?: Uuid | null;
    container_id?: Uuid | null;
  }>(
    ({ sessionId }) => `/ingestion/sessions/${sessionId}/destination`,
    "POST",
    ({ location_id, container_id }) => ({
      location_id: location_id ?? null,
      container_id: container_id ?? null,
    }),
  );
}

export function useConfirmIngestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, idempotencyKey }: { sessionId: Uuid; idempotencyKey?: string }) =>
      apiFetch<ConfirmResult>(`/ingestion/sessions/${sessionId}/confirm`, {
        method: "POST",
        body: { idempotency_key: idempotencyKey ?? null },
        offline: idempotencyKey
          ? { label: "Article ajouté (Blitz / Smart Input)", idempotencyKey }
          : undefined,
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(ingestionKeys.session(result.session.id), result.session);
      // Confirming moves stock, so everything derived from the ledger is stale.
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
      void queryClient.invalidateQueries({ queryKey: ["stock"] });
      void queryClient.invalidateQueries({ queryKey: ["transactions"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}


/* ------------------------------------------------------------ Assets (PHASE 7) */

export interface AssetParams {
  [key: string]: unknown;
  q?: string;
  product_id?: Uuid;
  lifecycle?: AssetLifecycle;
  condition?: AssetCondition;
  location_id?: Uuid;
  container_id?: Uuid;
  available_only?: boolean;
  limit?: number;
  offset?: number;
}

export const assetKeys = {
  list: (params: object) => ["assets", "list", params] as const,
  one: (id: Uuid) => ["assets", id] as const,
};

export function useAssets(params: AssetParams, options?: QueryOpts<Page<Asset>>) {
  return useQuery({
    queryKey: assetKeys.list(params),
    queryFn: get<Page<Asset>>("/assets", { query: params }),
    ...options,
  });
}

export function useAsset(assetId: Uuid, options?: QueryOpts<Asset>) {
  return useQuery({
    queryKey: assetKeys.one(assetId),
    queryFn: get<Asset>(`/assets/${assetId}`),
    enabled: Boolean(assetId),
    ...options,
  });
}

export interface CreateAssetInput {
  product_id: Uuid;
  name?: string | null;
  serial_number?: string | null;
  condition?: AssetCondition;
  lifecycle?: AssetLifecycle;
  ownership?: string;
  location_id?: Uuid | null;
  container_id?: Uuid | null;
  acquisition_date?: string | null;
  acquisition_precision?: string;
  acquisition_cost?: string | null;
  provenance?: string | null;
  notes?: string | null;
}

export function useCreateAsset(options?: UseMutationOptions<Asset, Error, CreateAssetInput>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAssetInput) =>
      apiFetch<Asset>("/assets", { method: "POST", body: input }),
    ...options,
    onSuccess: (data, variables, context) => {
      void queryClient.invalidateQueries({ queryKey: ["assets"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
      (options?.onSuccess as ((...args: unknown[]) => void) | undefined)?.(
        data,
        variables,
        context,
      );
    },
  });
}

/** Every asset mutation replaces the cached row: the server decides availability. */
function useAssetMutation<TInput extends { assetId: Uuid }>(
  path: (input: TInput) => string,
  body: (input: TInput) => unknown,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<Asset>(path(input), { method: "POST", body: body(input) }),
    onSuccess: (data) => {
      queryClient.setQueryData(assetKeys.one(data.id), data);
      void queryClient.invalidateQueries({ queryKey: ["assets", "list"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useMoveAsset() {
  return useAssetMutation<{
    assetId: Uuid;
    location_id?: Uuid | null;
    container_id?: Uuid | null;
  }>(
    ({ assetId }) => `/assets/${assetId}/move`,
    ({ location_id, container_id }) => ({
      location_id: location_id ?? null,
      container_id: container_id ?? null,
    }),
  );
}

export function useSetAssetCondition() {
  return useAssetMutation<{
    assetId: Uuid;
    condition?: AssetCondition;
    lifecycle?: AssetLifecycle;
    notes?: string;
  }>(
    ({ assetId }) => `/assets/${assetId}/condition`,
    ({ condition, lifecycle, notes }) => ({
      condition: condition ?? null,
      lifecycle: lifecycle ?? null,
      notes: notes ?? null,
    }),
  );
}

// --- People and loans (TICKET 024) ------------------------------------------

export const loanKeys = {
  list: (params: object) => ["loans", "list", params] as const,
  one: (id: Uuid) => ["loans", id] as const,
  summary: ["loans", "summary"] as const,
  people: (params: object) => ["people", "list", params] as const,
  holding: ["people", "holding"] as const,
};

export interface LoanParams {
  [key: string]: unknown;
  status?: LoanStatus;
  person_id?: Uuid;
  asset_id?: Uuid;
  overdue_only?: boolean;
  limit?: number;
  offset?: number;
}

export function useLoans(params: LoanParams, options?: QueryOpts<Page<Loan>>) {
  return useQuery({
    queryKey: loanKeys.list(params),
    queryFn: get<Page<Loan>>("/loans", { query: params }),
    ...options,
  });
}

export function useLoanSummary(options?: QueryOpts<LoanSummary>) {
  return useQuery({
    queryKey: loanKeys.summary,
    queryFn: get<LoanSummary>("/loans/summary"),
    ...options,
  });
}

export function usePeople(
  params: { search?: string; include_inactive?: boolean; limit?: number },
  options?: QueryOpts<Page<Person>>,
) {
  return useQuery({
    queryKey: loanKeys.people(params),
    queryFn: get<Page<Person>>("/people", { query: params }),
    ...options,
  });
}

export function usePeopleHolding(options?: QueryOpts<PersonHolding[]>) {
  return useQuery({
    queryKey: loanKeys.holding,
    queryFn: get<PersonHolding[]>("/people/holding"),
    ...options,
  });
}

/**
 * Every loan mutation invalidates the assets as well.
 *
 * Lending a tool changes whether it is available, and that answer lives on the
 * asset. A cache that updated the loan list but left the assets screen saying
 * "disponible" would be the client inventing its own availability rule, which
 * is the one thing the frontend must never do.
 */
function useLoanMutation<TInput>(
  path: (input: TInput) => string,
  body: (input: TInput) => unknown,
  method: "POST" = "POST",
  offline?: (input: TInput) => { label: string; idempotencyKey: string } | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<Loan>(path(input), {
        method,
        body: body(input),
        offline: offline?.(input),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(loanKeys.one(data.id), data);
      void queryClient.invalidateQueries({ queryKey: ["loans"] });
      void queryClient.invalidateQueries({ queryKey: ["assets"] });
      void queryClient.invalidateQueries({ queryKey: ["people"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export interface CheckOutInput {
  asset_id: Uuid;
  person_id: Uuid;
  due_on?: string | null;
  condition_out?: AssetCondition | null;
  notes?: string | null;
  idempotency_key?: string;
}

export function useCheckOut() {
  return useLoanMutation<CheckOutInput>(
    () => "/loans",
    (input) => ({
      asset_id: input.asset_id,
      person_id: input.person_id,
      due_on: input.due_on || null,
      condition_out: input.condition_out ?? null,
      notes: input.notes || null,
      idempotency_key: input.idempotency_key,
    }),
    "POST",
    (input) =>
      input.idempotency_key
        ? { label: "Prêt d'un outil", idempotencyKey: input.idempotency_key }
        : undefined,
  );
}

export function useReturnLoan() {
  return useLoanMutation<{
    loanId: Uuid;
    condition_in?: AssetCondition | null;
    notes?: string | null;
  }>(
    ({ loanId }) => `/loans/${loanId}/return`,
    ({ condition_in, notes }) => ({
      condition_in: condition_in ?? null,
      notes: notes || null,
    }),
  );
}

export function useExtendLoan() {
  return useLoanMutation<{ loanId: Uuid; due_on: string }>(
    ({ loanId }) => `/loans/${loanId}/extend`,
    ({ due_on }) => ({ due_on }),
  );
}

export interface CreatePersonInput {
  display_name: string;
  person_type?: PersonType;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
}

export function useCreatePerson() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePersonInput) =>
      apiFetch<Person>("/people", {
        method: "POST",
        body: {
          display_name: input.display_name,
          person_type: input.person_type ?? "EMPLOYEE",
          email: input.email || null,
          phone: input.phone || null,
          notes: input.notes || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["people"] });
    },
  });
}

// --- Projects (TICKET 025) --------------------------------------------------

export const projectKeys = {
  list: (params: object) => ["projects", "list", params] as const,
  one: (id: Uuid) => ["projects", id] as const,
  shortfall: (id: Uuid) => ["projects", id, "shortfall"] as const,
};

export interface ProjectParams {
  [key: string]: unknown;
  search?: string;
  status?: ProjectStatus;
  limit?: number;
  offset?: number;
}

export function useProjects(params: ProjectParams, options?: QueryOpts<Page<Project>>) {
  return useQuery({
    queryKey: projectKeys.list(params),
    queryFn: get<Page<Project>>("/projects", { query: params }),
    ...options,
  });
}

export function useProject(projectId: Uuid, options?: QueryOpts<ProjectDetail>) {
  return useQuery({
    queryKey: projectKeys.one(projectId),
    queryFn: get<ProjectDetail>(`/projects/${projectId}`),
    enabled: Boolean(projectId),
    ...options,
  });
}

export function useProjectShortfall(projectId: Uuid, options?: QueryOpts<CoverageLine[]>) {
  return useQuery({
    queryKey: projectKeys.shortfall(projectId),
    queryFn: get<CoverageLine[]>(`/projects/${projectId}/shortfall`),
    enabled: Boolean(projectId),
    ...options,
  });
}

export interface CreateProjectInput {
  name: string;
  reference?: string | null;
  client_name?: string | null;
  status?: ProjectStatus;
  starts_on?: string | null;
  ends_on?: string | null;
  notes?: string | null;
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectInput) =>
      apiFetch<Project>("/projects", {
        method: "POST",
        body: {
          name: input.name,
          reference: input.reference || null,
          client_name: input.client_name || null,
          status: input.status ?? "PLANNED",
          starts_on: input.starts_on || null,
          ends_on: input.ends_on || null,
          notes: input.notes || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

/**
 * Anything that touches a project's coverage also touches the stock.
 *
 * Reserving for CP16 changes what every other screen may promise. A cache that
 * refreshed the project but left the inventory saying the same 500 units were
 * available would be the client contradicting the ledger.
 */
function useProjectMutation<TInput extends { projectId: Uuid }, TResult>(
  path: (input: TInput) => string,
  method: "POST" | "PUT" | "DELETE",
  body?: (input: TInput) => unknown,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<TResult>(path(input), {
        method,
        body: body ? body(input) : undefined,
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.one(variables.projectId) });
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useSetRequirement() {
  return useProjectMutation<
    { projectId: Uuid; product_id: Uuid; quantity: string; notes?: string | null },
    ProjectDetail
  >(
    ({ projectId }) => `/projects/${projectId}/requirements`,
    "PUT",
    ({ product_id, quantity, notes }) => ({
      product_id,
      quantity,
      notes: notes || null,
    }),
  );
}

export function useRemoveRequirement() {
  return useProjectMutation<{ projectId: Uuid; requirementId: Uuid }, ProjectDetail>(
    ({ projectId, requirementId }) => `/projects/${projectId}/requirements/${requirementId}`,
    "DELETE",
  );
}

export function useReserveForProject() {
  return useProjectMutation<{ projectId: Uuid; requirementId?: Uuid }, ReservationResult>(
    ({ projectId, requirementId }) =>
      `/projects/${projectId}/reserve${requirementId ? `?requirement_id=${requirementId}` : ""}`,
    "POST",
  );
}

export function useReleaseProjectReservations() {
  return useProjectMutation<{ projectId: Uuid; requirementId?: Uuid }, ReservationResult>(
    ({ projectId, requirementId }) =>
      `/projects/${projectId}/release${requirementId ? `?requirement_id=${requirementId}` : ""}`,
    "POST",
  );
}

// --- Purchasing and receiving (TICKET 026) ----------------------------------

export const purchasingKeys = {
  suppliers: (params: object) => ["suppliers", params] as const,
  orders: (params: object) => ["purchase-orders", "list", params] as const,
  order: (id: Uuid) => ["purchase-orders", id] as const,
  receivable: ["purchase-orders", "receivable"] as const,
  receipts: (params: object) => ["receipts", params] as const,
};

export function useSuppliers(
  params: { search?: string; include_inactive?: boolean; limit?: number },
  options?: QueryOpts<Page<Supplier>>,
) {
  return useQuery({
    queryKey: purchasingKeys.suppliers(params),
    queryFn: get<Page<Supplier>>("/suppliers", { query: params }),
    ...options,
  });
}

export function useCreateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; contact_name?: string | null; phone?: string | null }) =>
      apiFetch<Supplier>("/suppliers", {
        method: "POST",
        body: {
          name: input.name,
          contact_name: input.contact_name || null,
          phone: input.phone || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["suppliers"] });
    },
  });
}

export interface OrderParams {
  [key: string]: unknown;
  search?: string;
  supplier_id?: Uuid;
  status?: PurchaseOrderStatus;
  limit?: number;
  offset?: number;
}

export function usePurchaseOrders(
  params: OrderParams,
  options?: QueryOpts<Page<PurchaseOrder>>,
) {
  return useQuery({
    queryKey: purchasingKeys.orders(params),
    queryFn: get<Page<PurchaseOrder>>("/purchase-orders", { query: params }),
    ...options,
  });
}

export function usePurchaseOrder(orderId: Uuid, options?: QueryOpts<PurchaseOrderDetail>) {
  return useQuery({
    queryKey: purchasingKeys.order(orderId),
    queryFn: get<PurchaseOrderDetail>(`/purchase-orders/${orderId}`),
    enabled: Boolean(orderId),
    ...options,
  });
}

export function useReceivableOrders(options?: QueryOpts<PurchaseOrder[]>) {
  return useQuery({
    queryKey: purchasingKeys.receivable,
    queryFn: get<PurchaseOrder[]>("/purchase-orders/receivable"),
    ...options,
  });
}

export function useReceipts(
  params: { order_id?: Uuid; limit?: number },
  options?: QueryOpts<Page<Receipt>>,
) {
  return useQuery({
    queryKey: purchasingKeys.receipts(params),
    queryFn: get<Page<Receipt>>("/receipts", { query: params }),
    ...options,
  });
}

/**
 * Anything that touches an order also touches the stock.
 *
 * A receipt posts RECEIVE movements: the inventory, the dashboard and every
 * screen that shows a quantity are stale the instant it lands.
 */
function usePurchasingMutation<TInput extends { orderId?: Uuid }, TResult>(
  path: (input: TInput) => string,
  method: "POST" | "PUT" | "DELETE",
  body?: (input: TInput) => unknown,
  offline?: (input: TInput) => { label: string; idempotencyKey: string } | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<TResult>(path(input), {
        method,
        body: body ? body(input) : undefined,
        offline: offline?.(input),
      }),
    onSuccess: (_data, variables) => {
      if (variables.orderId) {
        void queryClient.invalidateQueries({ queryKey: purchasingKeys.order(variables.orderId) });
      }
      void queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      void queryClient.invalidateQueries({ queryKey: ["receipts"] });
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useCreatePurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      supplier_id: Uuid;
      reference?: string | null;
      expected_on?: string | null;
    }) =>
      apiFetch<PurchaseOrder>("/purchase-orders", {
        method: "POST",
        body: {
          supplier_id: input.supplier_id,
          reference: input.reference || null,
          expected_on: input.expected_on || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
    },
  });
}

export function useSetOrderLine() {
  return usePurchasingMutation<
    { orderId: Uuid; product_id: Uuid; quantity: string; unit_price?: string | null },
    PurchaseOrderDetail
  >(
    ({ orderId }) => `/purchase-orders/${orderId}/lines`,
    "PUT",
    ({ product_id, quantity, unit_price }) => ({
      product_id,
      quantity,
      unit_price: unit_price || null,
    }),
  );
}

export function useRemoveOrderLine() {
  return usePurchasingMutation<{ orderId: Uuid; lineId: Uuid }, PurchaseOrderDetail>(
    ({ orderId, lineId }) => `/purchase-orders/${orderId}/lines/${lineId}`,
    "DELETE",
  );
}

export function useSetOrderStatus() {
  return usePurchasingMutation<{ orderId: Uuid; status: PurchaseOrderStatus }, PurchaseOrder>(
    ({ orderId }) => `/purchase-orders/${orderId}/status`,
    "POST",
    ({ status }) => ({ status }),
  );
}

export interface ReceiptLineInput {
  line_id: Uuid;
  declared: string;
  accepted: string;
  rejected?: string;
  location_id?: Uuid | null;
  container_id?: Uuid | null;
  mismatch_reason?: string | null;
}

export function usePostReceipt() {
  return usePurchasingMutation<
    {
      orderId: Uuid;
      lines: ReceiptLineInput[];
      supplier_reference?: string | null;
      notes?: string | null;
      idempotency_key?: string;
    },
    Receipt
  >(
    ({ orderId }) => `/purchase-orders/${orderId}/receipts`,
    "POST",
    ({ lines, supplier_reference, notes, idempotency_key }) => ({
      lines,
      supplier_reference: supplier_reference || null,
      notes: notes || null,
      idempotency_key,
    }),
    // A loading dock with no signal is the normal case, not the exception.
    ({ idempotency_key }) =>
      idempotency_key
        ? { label: "Réception d'une livraison", idempotencyKey: idempotency_key }
        : undefined,
  );
}

// --- « Demander à HD-Vault » (TICKET 027) -----------------------------------

/**
 * Ask a question.
 *
 * A mutation hook for a read endpoint, deliberately: an answer is the result of
 * an action the operator took, not a cache entry to keep warm. Re-running it on
 * a window focus would silently replace an answer someone is reading.
 */
export function useAskAssistant() {
  return useMutation({
    mutationFn: (question: string) =>
      apiFetch<AssistantAnswer>("/assistant/ask", { method: "POST", body: { question } }),
  });
}

export function useAssistantSuggestions(options?: QueryOpts<{ questions: string[] }>) {
  return useQuery({
    queryKey: ["assistant", "suggestions"],
    queryFn: get<{ questions: string[] }>("/assistant/suggestions"),
    staleTime: 60 * 60 * 1000,
    ...options,
  });
}

/**
 * What the AI chain can actually do right now (§13).
 *
 * Polled rarely and kept out of the way: this is a diagnostic, not a dashboard
 * tile. It exists so that "the AI is bad today" and "the local model was
 * unreachable" stop being indistinguishable from the operator's side.
 */
export function useAIDiagnostics(options?: QueryOpts<AIDiagnostics>) {
  return useQuery({
    queryKey: ["ai", "diagnostics"],
    queryFn: get<AIDiagnostics>("/ai/diagnostics"),
    staleTime: 60 * 1000,
    ...options,
  });
}


/**
 * Les conditionnements d'un produit (PM-01).
 *
 * Requête séparée de la fiche produit, comme l'endpoint : un conditionnement se
 * gère indépendamment, et charger la liste à chaque lecture de produit la
 * ralentirait pour rien.
 */
export function useProductPackages(productId: Uuid, options?: QueryOpts<ProductPackage[]>) {
  return useQuery({
    queryKey: ["products", productId, "packages"],
    queryFn: get<ProductPackage[]>(`/products/${productId}/packages`),
    ...options,
  });
}

export function useCreateProductPackage(productId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      apiFetch<ProductPackage>(`/products/${productId}/packages`, {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["products", productId, "packages"] });
    },
  });
}


/* ------------------------------------------- Commercial produit (PM-02) */

export function useProductSuppliers(productId: Uuid, options?: QueryOpts<SupplierProduct[]>) {
  return useQuery({
    queryKey: ["products", productId, "suppliers"],
    queryFn: get<SupplierProduct[]>(`/products/${productId}/suppliers`),
    ...options,
  });
}

export function useProductPolicies(productId: Uuid, options?: QueryOpts<InventoryPolicy[]>) {
  return useQuery({
    queryKey: ["products", productId, "policies"],
    queryFn: get<InventoryPolicy[]>(`/products/${productId}/inventory-policies`),
    ...options,
  });
}

/**
 * L'historique de prix, le plus récent d'abord.
 *
 * Requête séparée : personne n'a besoin de l'historique pour lire une fiche, et
 * le charger à chaque fois la ralentirait pour rien.
 */
export function useProductPrices(productId: Uuid, options?: QueryOpts<ProductPrice[]>) {
  return useQuery({
    queryKey: ["products", productId, "prices"],
    queryFn: get<ProductPrice[]>(`/products/${productId}/prices`),
    ...options,
  });
}

/**
 * Les liens du produit — enregistrés d'un côté, déduits de l'autre (V1.5-07).
 *
 * `includeInferred` est faux par défaut : la déduction coûte des requêtes, et
 * la fiche produit n'en a pas besoin pour s'afficher.
 */
export function useProductRelations(
  productId: Uuid,
  options?: { includeInferred?: boolean } & QueryOpts<ProductRelations>,
) {
  const { includeInferred, ...queryOptions } = options ?? {};
  const suffix = includeInferred ? "?include_inferred=true" : "";
  return useQuery({
    queryKey: ["products", productId, "relations", { inferred: Boolean(includeInferred) }],
    queryFn: get<ProductRelations>(`/products/${productId}/relations${suffix}`),
    ...queryOptions,
  });
}

function invalidateRelations(
  queryClient: ReturnType<typeof useQueryClient>,
  productId: Uuid,
) {
  void queryClient.invalidateQueries({ queryKey: ["products", productId, "relations"] });
}

/** Poser un lien. Un technicien propose, un gestionnaire confirme. */
export function useRelateProducts(productId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProductRelationWrite) =>
      apiFetch<ProductRelation>(`/products/${productId}/relations`, {
        method: "POST",
        body: input,
      }),
    onSuccess: () => invalidateRelations(queryClient, productId),
  });
}

export function useConfirmRelation(productId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (relationId: Uuid) =>
      apiFetch<ProductRelation>(`/products/${productId}/relations/${relationId}/confirm`, {
        method: "POST",
      }),
    onSuccess: () => invalidateRelations(queryClient, productId),
  });
}

/**
 * Retirer un lien.
 *
 * Retirer n'est pas nier : le produit redevient un produit dont HD-Vault ne
 * sait rien sur cette compatibilité. Pour dire « ils ne vont pas ensemble », il
 * faut une relation `INCOMPATIBLE`.
 */
export function useRemoveRelation(productId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (relationId: Uuid) =>
      apiFetch<void>(`/products/${productId}/relations/${relationId}`, { method: "DELETE" }),
    onSuccess: () => invalidateRelations(queryClient, productId),
  });
}


/* ------------------------------------------ Capacités produit (V1.5-06) */

/** Le vocabulaire. `query` cherche aussi dans les synonymes. */
export function useCapabilities(params?: {
  query?: string;
  family?: string;
  builtin?: boolean;
  active?: boolean;
}) {
  const search = new URLSearchParams();
  if (params?.query) search.set("query", params.query);
  if (params?.family) search.set("family", params.family);
  if (params?.builtin !== undefined) search.set("builtin", String(params.builtin));
  if (params?.active !== undefined) search.set("active", String(params.active));
  const suffix = search.toString() ? `?${search.toString()}` : "";
  return useQuery({
    queryKey: ["capabilities", params ?? {}],
    queryFn: get<CapabilityPage>(`/capabilities${suffix}`),
  });
}

export function useProductCapabilities(productId: Uuid) {
  return useQuery({
    queryKey: ["products", productId, "capabilities"],
    queryFn: get<ProductCapabilityList>(`/products/${productId}/capabilities`),
  });
}

function invalidateCapabilities(
  queryClient: ReturnType<typeof useQueryClient>,
  productId: Uuid,
) {
  void queryClient.invalidateQueries({ queryKey: ["products", productId, "capabilities"] });
}

/** Poser une capacité. Un technicien propose, un gestionnaire confirme. */
export function useSetProductCapability(productId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProductCapabilityWrite) =>
      apiFetch<ProductCapability>(`/products/${productId}/capabilities`, {
        method: "POST",
        body: input,
      }),
    onSuccess: () => invalidateCapabilities(queryClient, productId),
  });
}

export function useConfirmProductCapability(productId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rowId: Uuid) =>
      apiFetch<ProductCapability>(`/products/${productId}/capabilities/${rowId}/confirm`, {
        method: "POST",
      }),
    onSuccess: () => invalidateCapabilities(queryClient, productId),
  });
}

/**
 * Retirer une capacité.
 *
 * Retirer n'est pas nier : le produit redevient un produit dont HD-Vault ne
 * sait rien sur ce point. Pour dire « il ne fait pas ça », il faut une ligne
 * `NOT_SUPPORTED`.
 */
export function useRemoveProductCapability(productId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rowId: Uuid) =>
      apiFetch<void>(`/products/${productId}/capabilities/${rowId}`, { method: "DELETE" }),
    onSuccess: () => invalidateCapabilities(queryClient, productId),
  });
}

/* --------------------------------------- Provenance et documents (PM-03) */

/**
 * D'où vient chaque valeur du produit.
 *
 * Requête séparée, et volontairement pas chargée avec la fiche : la source d'un
 * poids intéresse celui qui doute d'un poids, pas celui qui cherche un article
 * sur une tablette.
 */
export function useProductProvenance(
  productId: Uuid,
  params?: { fieldName?: string; includeSuperseded?: boolean },
  options?: QueryOpts<FieldProvenance[]>,
) {
  const query = new URLSearchParams();
  if (params?.fieldName) query.set("field_name", params.fieldName);
  if (params?.includeSuperseded) query.set("include_superseded", "true");
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  return useQuery({
    queryKey: ["products", productId, "provenance", suffix],
    queryFn: get<FieldProvenance[]>(`/products/${productId}/provenance${suffix}`),
    ...options,
  });
}

export interface DocumentMetadataInput {
  media_type?: MediaType;
  language?: string | null;
  revision?: string | null;
  document_number?: string | null;
  version?: string | null;
  effective_date?: string | null;
  source?: ValueSource | null;
  source_reference?: string | null;
  document_manufacturer?: string | null;
}

/**
 * Corriger la révision, la langue ou la source d'un document déjà déposé.
 *
 * Les octets ne bougent pas : c'est la fiche du document qu'on corrige, pas le
 * fichier. Une révision B qui devient une révision C est un nouveau dépôt.
 */
export function useUpdateDocumentMetadata(productId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ mediaId, changes }: { mediaId: Uuid; changes: DocumentMetadataInput }) =>
      apiFetch<Media>(`/products/${productId}/documents/${mediaId}`, {
        method: "PATCH",
        body: changes,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.productMedia(productId) });
    },
  });
}

/**
 * L'état d'indexation d'un document (V1.5-09 §30).
 *
 * Interrogé document par document plutôt que fondu dans la liste des médias :
 * l'indexation avance en arrière-plan, et un état qui ne se rafraîchit qu'au
 * rechargement de la page ferait croire qu'elle est bloquée.
 */
export function useDocumentIndexStatus(mediaId: Uuid, options?: QueryOpts<DocumentIndexStatus>) {
  return useQuery({
    queryKey: queryKeys.documentIndex(mediaId),
    queryFn: get<DocumentIndexStatus>(`/media/${mediaId}/index-status`),
    ...options,
  });
}

/**
 * Lancer — ou relancer — l'indexation d'un document.
 *
 * Le même appel pour les deux : indexer et réindexer sont la même opération, et
 * `force` est ce qui distingue « fais si ce n'est pas fait » de « refais ».
 */
export function useIndexDocument(productId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ mediaId, force }: { mediaId: Uuid; force?: boolean }) =>
      apiFetch<DocumentIndexStatus>(
        `/media/${mediaId}/index${force ? "?force=true" : ""}`,
        { method: "POST" },
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.documentIndex(variables.mediaId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.productMedia(productId) });
    },
  });
}

/**
 * Déposer un document sur un produit.
 *
 * Le même endpoint que les photos : ``Media`` est l'unique infrastructure de
 * fichiers, et un manuel PDF n'a pas besoin d'une seconde. Ce qui change, c'est
 * ce qu'on écrit dessus ensuite.
 */
export function useAddProductDocument(productId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, mediaType }: { file: File; mediaType: MediaType }) => {
      const form = new FormData();
      form.append("file", file);
      form.append("media_type", mediaType);
      return apiFetch<Media>(`/products/${productId}/media`, {
        method: "POST",
        formData: form,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.productMedia(productId) });
    },
  });
}

/* ------------------------------------------- Conversations (V1.5-01) */

export const conversationKeys = {
  all: ["assistant", "conversations"] as const,
  list: (status?: string) => ["assistant", "conversations", "list", status ?? "all"] as const,
  detail: (id: Uuid) => ["assistant", "conversations", id] as const,
  messages: (id: Uuid) => ["assistant", "conversations", id, "messages"] as const,
};

export function useConversations(
  params?: { status?: ConversationStatus; limit?: number },
  options?: QueryOpts<ConversationPage>,
) {
  const query = new URLSearchParams();
  if (params?.status) query.set("status", params.status);
  if (params?.limit) query.set("limit", String(params.limit));
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  return useQuery({
    queryKey: conversationKeys.list(params?.status),
    queryFn: get<ConversationPage>(`/assistant/conversations${suffix}`),
    ...options,
  });
}

export function useConversation(conversationId: Uuid, options?: QueryOpts<ConversationDetail>) {
  return useQuery({
    queryKey: conversationKeys.detail(conversationId),
    queryFn: get<ConversationDetail>(`/assistant/conversations/${conversationId}`),
    ...options,
  });
}

/**
 * L'historique, page par page, du plus ancien au plus récent.
 *
 * Curseur sur le rang du message et non décalage numérique : un fil grandit par
 * le bas pendant qu'on le remonte, et un `offset` ferait sauter ou répéter un
 * message à chaque nouveau tour.
 */
export function useConversationMessages(conversationId: Uuid, options?: { enabled?: boolean }) {
  return useInfiniteQuery({
    queryKey: conversationKeys.messages(conversationId),
    initialPageParam: null as number | null,
    queryFn: ({ pageParam }) => {
      const query = new URLSearchParams({ limit: "30" });
      if (pageParam !== null) query.set("before_sequence", String(pageParam));
      return apiFetch<ConversationMessagePage>(
        `/assistant/conversations/${conversationId}/messages?${query.toString()}`,
      );
    },
    getNextPageParam: (page) => page.next_before_sequence,
    enabled: options?.enabled ?? true,
  });
}

export function useCreateConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { title?: string }) =>
      apiFetch<Conversation>("/assistant/conversations", { method: "POST", body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.all });
    },
  });
}

/**
 * Envoyer un message, sans en perdre un seul.
 *
 * `client_message_id` est ce qui rend le rejeu sûr : la file hors ligne renvoie
 * l'opération telle quelle au retour du réseau, et le serveur rend le message
 * déjà écrit au lieu d'en créer un second (ADR 0009).
 */
export function useSendConversationMessage(conversationId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { content: string; clientMessageId: string; attachmentIds?: Uuid[] }) =>
      apiFetch<MessageExchange>(`/assistant/conversations/${conversationId}/messages`, {
        method: "POST",
        body: {
          content: input.content,
          client_message_id: input.clientMessageId,
          attachment_ids: input.attachmentIds ?? [],
        },
        // Une question **avec photo** n'entre pas dans la file hors ligne
        // (V1.5-05). La photo est montée par un appel séparé qui a besoin du
        // réseau : la mettre en file donnerait un « envoyé » qui désignerait
        // une pièce jointe jamais arrivée. Sans photo, rien ne change.
        ...(input.attachmentIds?.length
          ? {}
          : {
              offline: {
                label: `Message à HD-Vault : ${input.content.slice(0, 40)}`,
                idempotencyKey: input.clientMessageId,
              },
            }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.messages(conversationId) });
      void queryClient.invalidateQueries({ queryKey: conversationKeys.detail(conversationId) });
      void queryClient.invalidateQueries({ queryKey: conversationKeys.list() });
    },
  });
}

/**
 * Signer, refuser, ou corriger un geste proposé (V1.6-12.2).
 *
 * **Jamais mis en file hors ligne.** Une confirmation relit les faits au moment
 * où elle part : la rejouer plus tard exécuterait un geste décidé sur des
 * chiffres qui n'existent plus. Le bouton est désactivé hors ligne, et la carte
 * le dit.
 *
 * Le corps ne porte que l'issue et, au plus, une correction sur les champs que
 * la proposition nomme. Jamais une action entière : c'est le serveur qui garde
 * ce qui va s'exécuter.
 */
export function useDecideProposal(conversationId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { proposalId: Uuid; payload: DecisionPayload }) =>
      apiFetch<DecisionResult>(`/assistant/proposals/${input.proposalId}/decision`, {
        method: "POST",
        body: input.payload,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.messages(conversationId) });
    },
  });
}

/**
 * Déposer une photo dans un fil, avant de poser la question (V1.5-05).
 *
 * **Jamais mis en file hors ligne.** Une photo en attente d'envoi et une photo
 * envoyée ne sont pas la même chose, et afficher la seconde pour la première
 * ferait croire à quelqu'un sur un chantier qu'HD-Vault a vu son étiquette. Le
 * bouton est désactivé hors ligne, et l'écran le dit.
 */
export function useAddConversationAttachment(conversationId: Uuid) {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return apiFetch<ConversationAttachment>(
        `/assistant/conversations/${conversationId}/attachments`,
        { method: "POST", formData: form },
      );
    },
  });
}

export function useArchiveConversation(conversationId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { archived: boolean }) =>
      apiFetch<Conversation>(
        `/assistant/conversations/${conversationId}/${input.archived ? "archive" : "reopen"}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.all });
    },
  });
}

// --- Listes de matériel (V1.6-01) -------------------------------------------

export const materialListKeys = {
  list: (params: object) => ["material-lists", "list", params] as const,
  one: (id: Uuid) => ["material-lists", id] as const,
};

export interface MaterialListParams {
  [key: string]: unknown;
  search?: string;
  type?: MaterialListType;
  status?: MaterialListStatus;
  project_id?: Uuid;
  include_archived?: boolean;
  limit?: number;
  offset?: number;
}

export function useMaterialLists(
  params: MaterialListParams,
  options?: QueryOpts<Page<MaterialList>>,
) {
  return useQuery({
    queryKey: materialListKeys.list(params),
    queryFn: get<Page<MaterialList>>("/material-lists", { query: params }),
    ...options,
  });
}

export function useMaterialList(
  listId: Uuid,
  options?: QueryOpts<MaterialListDetail>,
) {
  return useQuery({
    queryKey: materialListKeys.one(listId),
    queryFn: get<MaterialListDetail>(`/material-lists/${listId}`),
    enabled: Boolean(listId),
    ...options,
  });
}

export interface CreateMaterialListInput {
  name: string;
  description?: string | null;
  list_type?: MaterialListType;
  project_id?: Uuid | null;
  preferred_supplier_id?: Uuid | null;
}

export function useCreateMaterialList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMaterialListInput) =>
      apiFetch<MaterialList>("/material-lists", {
        method: "POST",
        body: {
          name: input.name,
          description: input.description || null,
          list_type: input.list_type ?? "GENERAL",
          project_id: input.project_id || null,
          preferred_supplier_id: input.preferred_supplier_id || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["material-lists"] });
    },
  });
}

/**
 * A list changes nothing outside itself.
 *
 * Deliberately narrower than `useProjectMutation`: no inventory key, no
 * dashboard key. Nothing on a list moves stock, so invalidating those caches
 * would suggest a link that does not exist.
 */
function useMaterialListMutation<TInput extends { listId: Uuid }, TResult>(
  path: (input: TInput) => string,
  method: "POST" | "PATCH" | "DELETE",
  body?: (input: TInput) => unknown,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<TResult>(path(input), {
        method,
        body: body ? body(input) : undefined,
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: materialListKeys.one(variables.listId),
      });
      void queryClient.invalidateQueries({ queryKey: ["material-lists"] });
    },
  });
}

export interface MaterialListLineInput {
  description?: string | null;
  product_id?: Uuid | null;
  quantity: string;
  unit?: UnitCode | null;
  priority?: PlanningPriority;
  required_at?: string | null;
  note?: string | null;
}

export function useAddMaterialListLine() {
  return useMaterialListMutation<
    { listId: Uuid } & MaterialListLineInput,
    MaterialListDetail
  >(
    ({ listId }) => `/material-lists/${listId}/lines`,
    "POST",
    ({
      description,
      product_id,
      quantity,
      unit,
      priority,
      required_at,
      note,
    }) => ({
      description: description || null,
      product_id: product_id || null,
      quantity,
      unit: unit || null,
      priority: priority ?? "NORMAL",
      required_at: required_at || null,
      note: note || null,
    }),
  );
}

export function useUpdateMaterialListLine() {
  return useMaterialListMutation<
    {
      listId: Uuid;
      lineId: Uuid;
      patch: Partial<MaterialListLineInput> & {
        status?: MaterialListLineStatus;
      };
    },
    MaterialListDetail
  >(
    ({ listId, lineId }) => `/material-lists/${listId}/lines/${lineId}`,
    "PATCH",
    ({ patch }) => patch,
  );
}

export function useRemoveMaterialListLine() {
  return useMaterialListMutation<
    { listId: Uuid; lineId: Uuid },
    MaterialListDetail
  >(
    ({ listId, lineId }) => `/material-lists/${listId}/lines/${lineId}`,
    "DELETE",
  );
}

export function useUpdateMaterialList() {
  return useMaterialListMutation<
    {
      listId: Uuid;
      patch: Partial<CreateMaterialListInput> & { status?: MaterialListStatus };
    },
    MaterialListDetail
  >(
    ({ listId }) => `/material-lists/${listId}`,
    "PATCH",
    ({ patch }) => patch,
  );
}

export function useArchiveMaterialList() {
  return useMaterialListMutation<{ listId: Uuid }, MaterialListDetail>(
    ({ listId }) => `/material-lists/${listId}/archive`,
    "POST",
  );
}

// --- Estimations de projet (V1.6-01) ----------------------------------------

export const estimateKeys = {
  list: (params: object) => ["project-estimates", "list", params] as const,
  one: (id: Uuid) => ["project-estimates", id] as const,
  version: (id: Uuid, versionId: Uuid) =>
    ["project-estimates", id, versionId] as const,
};

export interface ProjectEstimateParams {
  [key: string]: unknown;
  search?: string;
  status?: ProjectEstimateStatus;
  project_id?: Uuid;
  include_archived?: boolean;
  limit?: number;
  offset?: number;
}

export function useProjectEstimates(
  params: ProjectEstimateParams,
  options?: QueryOpts<Page<ProjectEstimate>>,
) {
  return useQuery({
    queryKey: estimateKeys.list(params),
    queryFn: get<Page<ProjectEstimate>>("/project-estimates", {
      query: params,
    }),
    ...options,
  });
}

export function useProjectEstimate(
  estimateId: Uuid,
  options?: QueryOpts<ProjectEstimateDetail>,
) {
  return useQuery({
    queryKey: estimateKeys.one(estimateId),
    queryFn: get<ProjectEstimateDetail>(`/project-estimates/${estimateId}`),
    enabled: Boolean(estimateId),
    ...options,
  });
}

/** An older revision, read exactly as it was quoted. */
export function useEstimateVersion(
  estimateId: Uuid,
  versionId: Uuid | undefined,
  options?: QueryOpts<EstimateVersionDetail>,
) {
  return useQuery({
    queryKey: estimateKeys.version(estimateId, versionId ?? ("" as Uuid)),
    queryFn: get<EstimateVersionDetail>(
      `/project-estimates/${estimateId}/versions/${versionId}`,
    ),
    enabled: Boolean(estimateId && versionId),
    ...options,
  });
}

export interface CreateProjectEstimateInput {
  name: string;
  description?: string | null;
  project_id?: Uuid | null;
}

export function useCreateProjectEstimate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectEstimateInput) =>
      apiFetch<ProjectEstimate>("/project-estimates", {
        method: "POST",
        body: {
          name: input.name,
          description: input.description || null,
          project_id: input.project_id || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["project-estimates"] });
    },
  });
}

function useEstimateMutation<TInput extends { estimateId: Uuid }, TResult>(
  path: (input: TInput) => string,
  method: "POST" | "PATCH" | "DELETE",
  body?: (input: TInput) => unknown,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<TResult>(path(input), {
        method,
        body: body ? body(input) : undefined,
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: estimateKeys.one(variables.estimateId),
      });
      void queryClient.invalidateQueries({ queryKey: ["project-estimates"] });
    },
  });
}

export function useCreateEstimateVersion() {
  return useEstimateMutation<
    { estimateId: Uuid; copy_lines?: boolean; notes?: string | null },
    EstimateVersionDetail
  >(
    ({ estimateId }) => `/project-estimates/${estimateId}/versions`,
    "POST",
    ({ copy_lines, notes }) => ({
      copy_lines: copy_lines ?? false,
      notes: notes || null,
    }),
  );
}

export function useFinalizeEstimateVersion() {
  return useEstimateMutation<
    { estimateId: Uuid; versionId: Uuid },
    EstimateVersionDetail
  >(
    ({ estimateId, versionId }) =>
      `/project-estimates/${estimateId}/versions/${versionId}/finalize`,
    "POST",
  );
}

export interface EstimateLineInput {
  line_type?: EstimateLineType;
  description?: string | null;
  product_id?: Uuid | null;
  quantity: string;
  unit?: UnitCode | null;
  unit_cost?: string | null;
  /** A fraction: "0.10" is ten percent. Material only. */
  waste_factor?: string | null;
  allowance?: string | null;
  note?: string | null;
}

export function useAddEstimateLine() {
  return useEstimateMutation<
    { estimateId: Uuid; versionId: Uuid } & EstimateLineInput,
    EstimateVersionDetail
  >(
    ({ estimateId, versionId }) =>
      `/project-estimates/${estimateId}/versions/${versionId}/lines`,
    "POST",
    ({
      line_type,
      description,
      product_id,
      quantity,
      unit,
      unit_cost,
      waste_factor,
      allowance,
      note,
    }) => ({
      line_type: line_type ?? "MATERIAL",
      description: description || null,
      product_id: product_id || null,
      quantity,
      unit: unit || null,
      unit_cost: unit_cost || null,
      waste_factor: waste_factor || null,
      allowance: allowance || null,
      note: note || null,
    }),
  );
}

export function useUpdateEstimateLine() {
  return useEstimateMutation<
    {
      estimateId: Uuid;
      versionId: Uuid;
      lineId: Uuid;
      patch: Partial<EstimateLineInput>;
    },
    EstimateVersionDetail
  >(
    ({ estimateId, versionId, lineId }) =>
      `/project-estimates/${estimateId}/versions/${versionId}/lines/${lineId}`,
    "PATCH",
    ({ patch }) => patch,
  );
}

export function useRemoveEstimateLine() {
  return useEstimateMutation<
    { estimateId: Uuid; versionId: Uuid; lineId: Uuid },
    EstimateVersionDetail
  >(
    ({ estimateId, versionId, lineId }) =>
      `/project-estimates/${estimateId}/versions/${versionId}/lines/${lineId}`,
    "DELETE",
  );
}

// --- Besoins confirmés, besoin net, suggestions (V1.6-02) -------------------

export const requirementKeys = {
  list: (projectId: Uuid) => ["projects", projectId, "requirements"] as const,
  net: (projectId: Uuid, scope?: Uuid) =>
    ["projects", projectId, "requirements", "net", scope ?? null] as const,
  suggestions: (projectId: Uuid) => ["projects", projectId, "suggestions"] as const,
  preview: (projectId: Uuid, listId: Uuid) =>
    ["projects", projectId, "confirm-preview", listId] as const,
};

export function useRequirements(projectId: Uuid, options?: QueryOpts<RequirementPage>) {
  return useQuery({
    queryKey: requirementKeys.list(projectId),
    queryFn: get<RequirementPage>(`/projects/${projectId}/requirements`),
    enabled: Boolean(projectId),
    ...options,
  });
}

/**
 * Le besoin net.
 *
 * Volontairement sans `staleTime` : chaque terme dérive du ledger et vieillit
 * à la transaction suivante. Une réponse gardée « parce qu'elle a moins d'une
 * minute » ferait dire à l'écran qu'il reste du stock qu'un autre chantier
 * vient de réserver.
 */
export function useNetRequirements(
  projectId: Uuid,
  fromLocationId?: Uuid,
  options?: QueryOpts<NetRequirements>,
) {
  return useQuery({
    queryKey: requirementKeys.net(projectId, fromLocationId),
    queryFn: get<NetRequirements>(`/projects/${projectId}/requirements/net`, {
      query: fromLocationId ? { from_location_id: fromLocationId } : undefined,
    }),
    enabled: Boolean(projectId),
    staleTime: 0,
    ...options,
  });
}

export function useRequirementSuggestions(
  projectId: Uuid,
  options?: QueryOpts<SuggestionPage>,
) {
  return useQuery({
    queryKey: requirementKeys.suggestions(projectId),
    queryFn: get<SuggestionPage>(`/projects/${projectId}/requirement-suggestions`),
    enabled: Boolean(projectId),
    ...options,
  });
}

export function useConfirmPreview(
  projectId: Uuid,
  listId: Uuid | undefined,
  options?: QueryOpts<ConfirmPreview>,
) {
  return useQuery({
    queryKey: requirementKeys.preview(projectId, listId ?? ("" as Uuid)),
    queryFn: get<ConfirmPreview>(
      `/projects/${projectId}/material-lists/${listId}/preview`,
    ),
    enabled: Boolean(projectId && listId),
    ...options,
  });
}

/**
 * Tout ce qui touche un besoin touche le besoin net.
 *
 * L'inventaire est invalidé aussi : accepter une suggestion ne bouge rien,
 * mais confirmer une liste peut mener à réserver, et un écran d'inventaire qui
 * garderait l'ancien disponible contredirait le ledger.
 */
function useRequirementMutation<TInput extends { projectId: Uuid }, TResult>(
  path: (input: TInput) => string,
  method: "POST" | "PATCH" | "DELETE",
  body?: (input: TInput) => unknown,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<TResult>(path(input), { method, body: body ? body(input) : undefined }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["projects", variables.projectId] });
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
    },
  });
}

export interface CreateRequirementInput {
  projectId: Uuid;
  product_id?: Uuid | null;
  description?: string | null;
  quantity: string;
  unit?: UnitCode | null;
  required_at?: string | null;
  priority?: PlanningPriority;
  notes?: string | null;
}

export function useCreateRequirement() {
  return useRequirementMutation<CreateRequirementInput, RequirementPage>(
    ({ projectId }) => `/projects/${projectId}/requirements`,
    "POST",
    ({ product_id, description, quantity, unit, required_at, priority, notes }) => ({
      product_id: product_id || null,
      description: description || null,
      quantity,
      unit: unit || null,
      required_at: required_at || null,
      priority: priority ?? "NORMAL",
      notes: notes || null,
    }),
  );
}

export function useUpdateRequirement() {
  return useRequirementMutation<
    {
      projectId: Uuid;
      requirementId: Uuid;
      patch: Partial<Omit<CreateRequirementInput, "projectId">> & {
        state?: ProjectRequirementState;
      };
    },
    RequirementPage
  >(
    ({ projectId, requirementId }) => `/projects/${projectId}/requirements/${requirementId}`,
    "PATCH",
    ({ patch }) => patch,
  );
}

export function useConfirmMaterialList() {
  return useRequirementMutation<
    { projectId: Uuid; listId: Uuid; line_ids?: Uuid[] | null },
    RequirementPage
  >(
    ({ projectId, listId }) => `/projects/${projectId}/material-lists/${listId}/confirm`,
    "POST",
    ({ line_ids }) => ({ line_ids: line_ids ?? null }),
  );
}

export function useAnalyseCompleteness() {
  return useRequirementMutation<{ projectId: Uuid }, SuggestionPage>(
    ({ projectId }) => `/projects/${projectId}/requirements/analyze-completeness`,
    "POST",
  );
}

export function useAcceptSuggestion() {
  return useRequirementMutation<
    { projectId: Uuid; suggestionId: Uuid; quantity: string },
    SuggestionPage
  >(
    ({ projectId, suggestionId }) =>
      `/projects/${projectId}/requirement-suggestions/${suggestionId}/accept`,
    "POST",
    ({ quantity }) => ({ quantity }),
  );
}

export function useRejectSuggestion() {
  return useRequirementMutation<
    { projectId: Uuid; suggestionId: Uuid },
    SuggestionPage
  >(
    ({ projectId, suggestionId }) =>
      `/projects/${projectId}/requirement-suggestions/${suggestionId}/reject`,
    "POST",
  );
}

// --- Politiques de réapprovisionnement (V1.6-03) ----------------------------

export const policyKeys = {
  list: (params: object) => ["inventory-policies", "list", params] as const,
  one: (id: Uuid) => ["inventory-policies", id] as const,
  recommendations: (id: Uuid) => ["inventory-policies", id, "recommendations"] as const,
  history: (id: Uuid) => ["inventory-policies", id, "history"] as const,
};

export interface PolicyParams {
  [key: string]: unknown;
  location_id?: Uuid;
  product_id?: Uuid;
  mode?: PolicyMode;
  limit?: number;
  offset?: number;
}

export function useInventoryPolicies(
  params: PolicyParams,
  options?: QueryOpts<Page<InventoryPolicy>>,
) {
  return useQuery({
    queryKey: policyKeys.list(params),
    queryFn: get<Page<InventoryPolicy>>("/inventory-policies", { query: params }),
    ...options,
  });
}

export function usePolicyRecommendations(
  policyId: Uuid | undefined,
  options?: QueryOpts<PolicyRecommendationList>,
) {
  return useQuery({
    queryKey: policyKeys.recommendations(policyId ?? ("" as Uuid)),
    queryFn: get<PolicyRecommendationList>(
      `/inventory-policies/${policyId}/recommendations`,
    ),
    enabled: Boolean(policyId),
    ...options,
  });
}

export function usePolicyHistory(
  policyId: Uuid | undefined,
  options?: QueryOpts<PolicyChangeList>,
) {
  return useQuery({
    queryKey: policyKeys.history(policyId ?? ("" as Uuid)),
    queryFn: get<PolicyChangeList>(`/inventory-policies/${policyId}/history`),
    enabled: Boolean(policyId),
    ...options,
  });
}

/**
 * Tout ce qui touche une politique touche ce qu'elle propose et son historique.
 *
 * L'inventaire n'est **pas** invalidé : un seuil ne bouge aucune quantité. Le
 * faire suggérerait un lien qui n'existe pas.
 */
function usePolicyMutation<TInput extends { policyId: Uuid }, TResult>(
  path: (input: TInput) => string,
  body?: (input: TInput) => unknown,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<TResult>(path(input), { method: "POST", body: body ? body(input) : undefined }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["inventory-policies"] });
      void queryClient.invalidateQueries({ queryKey: policyKeys.one(variables.policyId) });
    },
  });
}

export function useAnalysePolicy() {
  return usePolicyMutation<{ policyId: Uuid; window_days?: number }, PolicyRecommendationList>(
    ({ policyId }) => `/inventory-policies/${policyId}/analyze`,
    ({ window_days }) => ({ window_days: window_days ?? 90 }),
  );
}

export function useAnalysePolicyBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { location_id?: Uuid; window_days?: number; limit?: number }) =>
      apiFetch<BatchAnalyseResult>("/inventory-policies/analyze", {
        method: "POST",
        body: {
          location_id: input.location_id ?? null,
          window_days: input.window_days ?? 90,
          limit: input.limit ?? 50,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["inventory-policies"] });
    },
  });
}

function useRecommendationMutation<TInput extends { policyId: Uuid; recommendationId: Uuid }>(
  action: "approve" | "reject" | "apply",
  body?: (input: TInput) => unknown,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<PolicyRecommendation>(
        `/inventory-policy-recommendations/${input.recommendationId}/${action}`,
        { method: "POST", body: body ? body(input) : undefined },
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["inventory-policies"] });
      void queryClient.invalidateQueries({
        queryKey: policyKeys.recommendations(variables.policyId),
      });
      void queryClient.invalidateQueries({ queryKey: policyKeys.history(variables.policyId) });
    },
  });
}

export function useApproveRecommendation() {
  return useRecommendationMutation("approve");
}

export function useRejectRecommendation() {
  return useRecommendationMutation<{
    policyId: Uuid;
    recommendationId: Uuid;
    reason?: string | null;
  }>("reject", ({ reason }) => ({ reason: reason || null }));
}

export function useApplyRecommendation() {
  return useRecommendationMutation("apply");
}

export function useRevertPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { policyId: Uuid; changeId: Uuid; note?: string | null }) =>
      apiFetch<PolicyChangeList>(`/inventory-policies/${input.policyId}/revert`, {
        method: "POST",
        body: { change_id: input.changeId, note: input.note || null },
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["inventory-policies"] });
      void queryClient.invalidateQueries({ queryKey: policyKeys.history(variables.policyId) });
    },
  });
}

// --- Demandes de prix (V1.6-04) ---------------------------------------------
//
// **Aucun de ces appels n'envoie quoi que ce soit.** Il n'existe pas de
// `useSendRfq`, parce qu'il n'existe pas de route derriere. `finalize` fige un
// contenu ; l'apercu le montre.

export const rfqKeys = {
  list: (params: object) => ["rfqs", "list", params] as const,
  one: (id: Uuid) => ["rfqs", id] as const,
  preview: (id: Uuid) => ["rfqs", id, "dispatch-preview"] as const,
};

export interface RfqParams {
  [key: string]: unknown;
  status?: RfqStatus;
  project_id?: Uuid;
  supplier_id?: Uuid;
  limit?: number;
  offset?: number;
}

export function useRfqs(params: RfqParams, options?: QueryOpts<Page<Rfq>>) {
  return useQuery({
    queryKey: rfqKeys.list(params),
    queryFn: get<Page<Rfq>>("/rfqs", { query: params }),
    ...options,
  });
}

export function useRfq(rfqId: Uuid, options?: QueryOpts<RfqDetail>) {
  return useQuery({
    queryKey: rfqKeys.one(rfqId),
    queryFn: get<RfqDetail>(`/rfqs/${rfqId}`),
    enabled: Boolean(rfqId),
    ...options,
  });
}

/**
 * Ce que chaque fournisseur lirait. Disponible sur un brouillon — c'est la
 * qu'on verifie ce qu'on s'apprete a figer — et marque non final.
 */
export function useRfqDispatchPreview(
  rfqId: Uuid,
  options?: QueryOpts<RfqDispatchPreview[]>,
) {
  return useQuery({
    queryKey: rfqKeys.preview(rfqId),
    queryFn: get<RfqDispatchPreview[]>(`/rfqs/${rfqId}/dispatch-preview`),
    enabled: Boolean(rfqId),
    ...options,
  });
}

/** Chez qui ces articles sont references. **Aucun classement.** */
export function useKnownSuppliers(
  productIds: Uuid[],
  options?: QueryOpts<KnownSupplier[]>,
) {
  return useQuery({
    queryKey: ["rfqs", "known-suppliers", productIds] as const,
    queryFn: get<KnownSupplier[]>("/rfqs/suppliers/known", {
      query: { product_id: productIds },
    }),
    enabled: productIds.length > 0,
    ...options,
  });
}

export interface CreateRfqInput {
  title: string;
  description?: string | null;
  project_id?: Uuid | null;
  required_at?: string | null;
  currency?: string | null;
  instructions?: string | null;
  requested_terms?: RfqRequestedTerm[];
}

export function useCreateRfq() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRfqInput) =>
      apiFetch<Rfq>("/rfqs", {
        method: "POST",
        body: {
          title: input.title,
          description: input.description || null,
          project_id: input.project_id || null,
          required_at: input.required_at || null,
          currency: input.currency || null,
          instructions: input.instructions || null,
          requested_terms: input.requested_terms ?? null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["rfqs"] });
    },
  });
}

function useRfqMutation<TInput extends { rfqId: Uuid }, TResult>(
  path: (input: TInput) => string,
  method: "POST" | "PATCH" | "PUT" | "DELETE",
  body?: (input: TInput) => unknown,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<TResult>(path(input), {
        method,
        body: body ? body(input) : undefined,
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: rfqKeys.one(variables.rfqId) });
      void queryClient.invalidateQueries({ queryKey: rfqKeys.preview(variables.rfqId) });
      void queryClient.invalidateQueries({ queryKey: ["rfqs", "list"] });
    },
  });
}

export function useUpdateRfq() {
  return useRfqMutation<
    {
      rfqId: Uuid;
      title?: string;
      description?: string | null;
      required_at?: string | null;
      instructions?: string | null;
      requested_terms?: RfqRequestedTerm[];
    },
    RfqDetail
  >(
    ({ rfqId }) => `/rfqs/${rfqId}`,
    "PATCH",
    ({ rfqId: _rfqId, ...rest }) => rest,
  );
}

export function useAddRfqSupplier() {
  return useRfqMutation<{ rfqId: Uuid; supplier_id: Uuid; note?: string | null }, RfqDetail>(
    ({ rfqId }) => `/rfqs/${rfqId}/suppliers`,
    "POST",
    ({ supplier_id, note }) => ({ supplier_id, note: note || null }),
  );
}

export function useRemoveRfqSupplier() {
  return useRfqMutation<{ rfqId: Uuid; rfqSupplierId: Uuid }, RfqDetail>(
    ({ rfqId, rfqSupplierId }) => `/rfqs/${rfqId}/suppliers/${rfqSupplierId}`,
    "DELETE",
  );
}

/** La liste complete des lignes que ce fournisseur recoit. */
export function useSetRfqAllocation() {
  return useRfqMutation<{ rfqId: Uuid; rfqSupplierId: Uuid; line_ids: Uuid[] }, RfqDetail>(
    ({ rfqId, rfqSupplierId }) => `/rfqs/${rfqId}/suppliers/${rfqSupplierId}/lines`,
    "PUT",
    ({ line_ids }) => ({ line_ids }),
  );
}

export interface RfqLineInput {
  description?: string | null;
  product_id?: Uuid | null;
  requested_quantity: Quantity;
  unit?: UnitCode | null;
  required_at?: string | null;
  note?: string | null;
}

export function useAddRfqLine() {
  return useRfqMutation<{ rfqId: Uuid } & RfqLineInput, RfqDetail>(
    ({ rfqId }) => `/rfqs/${rfqId}/lines`,
    "POST",
    ({ rfqId: _rfqId, ...rest }) => ({
      description: rest.description || null,
      product_id: rest.product_id || null,
      requested_quantity: rest.requested_quantity,
      unit: rest.unit || null,
      required_at: rest.required_at || null,
      note: rest.note || null,
    }),
  );
}

export function useUpdateRfqLine() {
  return useRfqMutation<
    {
      rfqId: Uuid;
      lineId: Uuid;
      description?: string;
      requested_quantity?: Quantity;
      unit?: UnitCode;
      required_at?: string | null;
      note?: string | null;
    },
    RfqLine
  >(
    ({ rfqId, lineId }) => `/rfqs/${rfqId}/lines/${lineId}`,
    "PATCH",
    ({ rfqId: _rfqId, lineId: _lineId, ...rest }) => rest,
  );
}

export function useRemoveRfqLine() {
  return useRfqMutation<{ rfqId: Uuid; lineId: Uuid }, RfqDetail>(
    ({ rfqId, lineId }) => `/rfqs/${rfqId}/lines/${lineId}`,
    "DELETE",
  );
}

/**
 * Reprendre la quantite du manque net **maintenant**, sur demande explicite.
 * Rien ne l'appelle tout seul : une quantite commerciale ne change pas parce
 * qu'un stock a bouge.
 */
export function useRefreshRfqLine() {
  return useRfqMutation<{ rfqId: Uuid; lineId: Uuid }, RfqDetail>(
    ({ rfqId, lineId }) => `/rfqs/${rfqId}/lines/${lineId}/refresh`,
    "POST",
  );
}

/** Figer le contenu. **N'envoie rien** — il n'existe aucune route d'envoi. */
export function useFinalizeRfq() {
  return useRfqMutation<{ rfqId: Uuid }, RfqDetail>(
    ({ rfqId }) => `/rfqs/${rfqId}/finalize`,
    "POST",
  );
}

export function useCancelRfq() {
  return useRfqMutation<{ rfqId: Uuid; reason?: string | null }, RfqDetail>(
    ({ rfqId }) => `/rfqs/${rfqId}/cancel`,
    "POST",
    ({ reason }) => ({ reason: reason || null }),
  );
}

export function useCreateRfqFromRequirements() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      project_id: Uuid;
      requirement_ids: Uuid[];
      title: string;
      required_at?: string | null;
    }) =>
      apiFetch<RfqDetail>("/rfqs/from-requirements", {
        method: "POST",
        body: { ...input, required_at: input.required_at || null },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["rfqs"] });
    },
  });
}

export function useCreateRfqFromMaterialList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      material_list_id: Uuid;
      line_ids?: Uuid[] | null;
      title: string;
    }) =>
      apiFetch<RfqDetail>("/rfqs/from-material-list", {
        method: "POST",
        body: { ...input, line_ids: input.line_ids ?? null },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["rfqs"] });
    },
  });
}

// --- Frontiere e-mail (V1.6-05) ---------------------------------------------
//
// Il n'existe **aucun hook automatique**. `useSendRfq` part d'un clic, apres
// une confirmation ou l'utilisateur a lu le destinataire, l'objet et le corps.
// Rien d'autre dans l'application n'appelle un envoi.

export const messagingKeys = {
  emailStatus: ["email", "status"] as const,
  outbound: (params: object) => ["outbound-messages", params] as const,
  outboundOne: (id: Uuid) => ["outbound-messages", id] as const,
  inbound: (params: object) => ["inbound-messages", params] as const,
  inboundOne: (id: Uuid) => ["inbound-messages", id] as const,
  dispatch: (rfqId: Uuid) => ["rfqs", rfqId, "dispatch"] as const,
  rfqOutbound: (rfqId: Uuid) => ["rfqs", rfqId, "outbound-messages"] as const,
};

/** Ce qui est branche. **Sans secret** : le backend n'en renvoie aucun. */
export function useEmailStatus(options?: QueryOpts<EmailStatus>) {
  return useQuery({
    queryKey: messagingKeys.emailStatus,
    queryFn: get<EmailStatus>("/email/status"),
    ...options,
  });
}

/** Ou en est la transmission, destinataire par destinataire. */
export function useRfqDispatch(rfqId: Uuid, options?: QueryOpts<RfqDispatchRow[]>) {
  return useQuery({
    queryKey: messagingKeys.dispatch(rfqId),
    queryFn: get<RfqDispatchRow[]>(`/rfqs/${rfqId}/dispatch`),
    enabled: Boolean(rfqId),
    ...options,
  });
}

export function useRfqOutboundMessages(
  rfqId: Uuid,
  options?: QueryOpts<OutboundMessage[]>,
) {
  return useQuery({
    queryKey: messagingKeys.rfqOutbound(rfqId),
    queryFn: get<OutboundMessage[]>(`/rfqs/${rfqId}/outbound-messages`),
    enabled: Boolean(rfqId),
    ...options,
  });
}

export interface OutboundParams {
  [key: string]: unknown;
  status?: OutboundMessageStatus;
  rfq_id?: Uuid;
  limit?: number;
  offset?: number;
}

export function useOutboundMessages(
  params: OutboundParams,
  options?: QueryOpts<Page<OutboundMessage>>,
) {
  return useQuery({
    queryKey: messagingKeys.outbound(params),
    queryFn: get<Page<OutboundMessage>>("/outbound-messages", { query: params }),
    ...options,
  });
}

export interface InboundParams {
  [key: string]: unknown;
  classification?: InboundClassification;
  rfq_id?: Uuid;
  limit?: number;
  offset?: number;
}

export function useInboundMessages(
  params: InboundParams,
  options?: QueryOpts<Page<InboundMessage>>,
) {
  return useQuery({
    queryKey: messagingKeys.inbound(params),
    queryFn: get<Page<InboundMessage>>("/inbound-messages", { query: params }),
    ...options,
  });
}

export function useInboundMessage(
  messageId: Uuid,
  options?: QueryOpts<InboundMessage>,
) {
  return useQuery({
    queryKey: messagingKeys.inboundOne(messageId),
    queryFn: get<InboundMessage>(`/inbound-messages/${messageId}`),
    enabled: Boolean(messageId),
    ...options,
  });
}

function invalidateMessaging(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ["outbound-messages"] });
  void queryClient.invalidateQueries({ queryKey: ["inbound-messages"] });
  void queryClient.invalidateQueries({ queryKey: ["rfqs"] });
}

/**
 * **Le seul chemin d'envoi de l'application.**
 *
 * Appele depuis une confirmation, jamais depuis un effet, un intervalle ou une
 * suggestion. La reponse porte l'etat reel de chaque message : avec le
 * fournisseur par defaut, elle dit « echec », pas « accepte ».
 */
export function useSendRfq() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { rfqId: Uuid; rfq_supplier_ids?: Uuid[] }) =>
      apiFetch<OutboundMessage[]>(`/rfqs/${input.rfqId}/send`, {
        method: "POST",
        body: { rfq_supplier_ids: input.rfq_supplier_ids ?? [] },
      }),
    onSuccess: () => invalidateMessaging(queryClient),
  });
}

/** Un echec net, sur demande. Jamais une livraison incertaine. */
export function useRetryOutbound() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { messageId: Uuid }) =>
      apiFetch<OutboundMessage>(`/outbound-messages/${input.messageId}/retry`, {
        method: "POST",
      }),
    onSuccess: () => invalidateMessaging(queryClient),
  });
}

export function useCancelOutbound() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { messageId: Uuid }) =>
      apiFetch<OutboundMessage>(`/outbound-messages/${input.messageId}/cancel`, {
        method: "POST",
      }),
    onSuccess: () => invalidateMessaging(queryClient),
  });
}

/** Une etiquette. **Ne cree aucune soumission.** */
export function useClassifyInbound() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { messageId: Uuid; classification: InboundClassification }) =>
      apiFetch<InboundMessage>(
        `/inbound-messages/${input.messageId}/classification`,
        { method: "PATCH", body: { classification: input.classification } },
      ),
    onSuccess: () => invalidateMessaging(queryClient),
  });
}

/** Une metadonnee : la demande de prix ne bouge pas. */
export function useLinkInboundRfq() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { messageId: Uuid; rfq_id: Uuid | null }) =>
      apiFetch<InboundMessage>(`/inbound-messages/${input.messageId}/link-rfq`, {
        method: "POST",
        body: { rfq_id: input.rfq_id },
      }),
    onSuccess: () => invalidateMessaging(queryClient),
  });
}
